import { createContext, useState, useCallback, useEffect, useRef } from "react";
import { queryClient } from "@/lib/queryClient";

export type ScanStatus = "idle" | "scanning" | "completed" | "failed";

interface ScannerContextValue {
  jobId: string | null;
  status: ScanStatus;
  minimized: boolean;
  dismissed: boolean;
  originPath: string;
  originFlow: string | null;
  result: any | null;
  errorMessage: string | null;
  reopenRequested: boolean;
  progressStep: string | null;
  progressPct: number | null;
  startScan: (jobId: string, originPath: string, originFlow?: string | null, startMinimized?: boolean) => void;
  setProgress: (step: string | null, pct: number | null) => void;
  minimize: () => void;
  dismiss: () => void;
  requestReopen: () => void;
  clearReopenRequest: () => void;
  clear: () => void;
}

export const ScannerContext = createContext<ScannerContextValue | null>(null);

export function ScannerProvider({ children }: { children: React.ReactNode }) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<ScanStatus>("idle");
  const [minimized, setMinimized] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [originPath, setOriginPath] = useState("/floorplan");
  const [originFlow, setOriginFlow] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reopenRequested, setReopenRequested] = useState(false);
  const [progressStep, setProgressStep] = useState<string | null>(null);
  const [progressPct, setProgressPct] = useState<number | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingDeadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastProgressTokenRef = useRef<string | null>(null);
  const completedRef = useRef(false);

  /** How long to poll without progress before giving up (8 minutes). */
  const BACKGROUND_POLL_TIMEOUT_MS = 8 * 60 * 1000;

  useEffect(() => {
    if (!jobId || status !== "scanning" || !minimized) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (pollingDeadlineRef.current) {
        clearTimeout(pollingDeadlineRef.current);
        pollingDeadlineRef.current = null;
      }
      return;
    }

    completedRef.current = false;
    lastProgressTokenRef.current = null;

    const fireTimeout = () => {
      if (!completedRef.current) {
        completedRef.current = true;
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        pollingDeadlineRef.current = null;
        setErrorMessage("Extraction is taking longer than expected — please try uploading again.");
        setProgressStep(null);
        setProgressPct(null);
        setStatus("failed");
      }
    };

    /** Arms (or re-arms) the inactivity deadline. Called on start and whenever progress advances. */
    const resetDeadline = () => {
      if (pollingDeadlineRef.current) clearTimeout(pollingDeadlineRef.current);
      pollingDeadlineRef.current = setTimeout(fireTimeout, BACKGROUND_POLL_TIMEOUT_MS);
    };

    const poll = async () => {
      if (completedRef.current) return;
      try {
        const res = await fetch(`/api/parse-floorplan/status/${jobId}`, { credentials: "include" });
        const data = await res.json();
        if (data.status === "done") {
          completedRef.current = true;
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
          if (pollingDeadlineRef.current) { clearTimeout(pollingDeadlineRef.current); pollingDeadlineRef.current = null; }
          setResult(data.result);
          setProgressStep(null);
          setProgressPct(null);
          setStatus("completed");
          // Refresh all data the completed scan may have changed so pages
          // (Floorplan, Inventory Analysis) update without a manual reload.
          queryClient.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
          queryClient.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
          queryClient.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
          queryClient.invalidateQueries({ queryKey: ["/api/lenders"] });
          queryClient.invalidateQueries({
            predicate: (q) => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/lenders/comparison-stats"),
          });
          // A completed scan must always have a visible review path: un-hide the
          // pill even if the user dismissed it while the scan was running.
          setDismissed(false);
        } else if (data.status === "canceled") {
          // Job was canceled (e.g. from another tab/session) — remove the pill entirely.
          completedRef.current = true;
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
          if (pollingDeadlineRef.current) { clearTimeout(pollingDeadlineRef.current); pollingDeadlineRef.current = null; }
          setResult(null);
          setErrorMessage(null);
          setProgressStep(null);
          setProgressPct(null);
          setStatus("idle");
          setJobId(null);
        } else if (data.status === "error") {
          completedRef.current = true;
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
          if (pollingDeadlineRef.current) { clearTimeout(pollingDeadlineRef.current); pollingDeadlineRef.current = null; }
          setErrorMessage(data.error || "Extraction failed");
          setProgressStep(null);
          setProgressPct(null);
          setStatus("failed");
        } else if (data.status === "processing") {
          // Reset the inactivity deadline whenever progress actually advances.
          const token = `${data.step ?? ""}|${data.pct ?? ""}`;
          if (token !== lastProgressTokenRef.current) {
            lastProgressTokenRef.current = token;
            resetDeadline();
          }
          if (data.step != null) setProgressStep(data.step);
          if (data.pct != null) setProgressPct(data.pct);
        }
      } catch {
        // silently ignore transient network errors during background polling
      }
    };

    // Arm the initial inactivity deadline.
    resetDeadline();

    pollingRef.current = setInterval(poll, 2000);
    return () => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      if (pollingDeadlineRef.current) { clearTimeout(pollingDeadlineRef.current); pollingDeadlineRef.current = null; }
    };
  }, [jobId, status, minimized]);

  const startScan = useCallback((newJobId: string, path: string, flow?: string | null, startMinimized = false) => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    completedRef.current = false;
    setJobId(newJobId);
    setStatus("scanning");
    setMinimized(startMinimized);
    setDismissed(false);
    setOriginPath(path);
    setOriginFlow(flow ?? null);
    setResult(null);
    setErrorMessage(null);
    setReopenRequested(false);
    setProgressStep(null);
    setProgressPct(null);
  }, []);

  const setProgress = useCallback((step: string | null, pct: number | null) => {
    if (step !== null) setProgressStep(step);
    if (pct !== null) setProgressPct(pct);
  }, []);

  const minimize = useCallback(() => setMinimized(true), []);

  const dismiss = useCallback(() => setDismissed(true), []);

  const requestReopen = useCallback(() => {
    setReopenRequested(true);
    setDismissed(false);
  }, []);

  const clearReopenRequest = useCallback(() => setReopenRequested(false), []);

  const clear = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (pollingDeadlineRef.current) { clearTimeout(pollingDeadlineRef.current); pollingDeadlineRef.current = null; }
    completedRef.current = false;
    setJobId(null);
    setStatus("idle");
    setMinimized(false);
    setDismissed(false);
    setOriginPath("/floorplan");
    setOriginFlow(null);
    setResult(null);
    setErrorMessage(null);
    setReopenRequested(false);
    setProgressStep(null);
    setProgressPct(null);
  }, []);

  return (
    <ScannerContext.Provider value={{
      jobId, status, minimized, dismissed, originPath, originFlow, result, errorMessage, reopenRequested,
      progressStep, progressPct,
      startScan, setProgress, minimize, dismiss, requestReopen, clearReopenRequest, clear,
    }}>
      {children}
    </ScannerContext.Provider>
  );
}

